import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Breadcrumb, Card, Col, Row, Spinner, Alert, Button } from 'react-bootstrap';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import config from '../../config';
import { useAuth } from '../../context/AuthContext';
import {
    applyOptimisticPinToTasks,
    formatTimelineScopeRange,
    stampGanttQueueOrder,
    restampGanttQueueOrder,
} from './ganttHelpers';
import TeamStyleGanttChart, {
    useTeamStyleTimeline,
    userFullName,
} from '../../components/TeamStyleGanttChart';
import TimelineRunOrder from '../../components/TimelineRunOrder';
import '../../assets/css/user-gantt.css';
import '../../assets/css/team-planner.css';
import '../../assets/css/timeline-ui.css';

const reorderList = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

const UserGantt = () => {
    const { accessToken, userId, username } = useAuth();
    const navigate = useNavigate();
    const [tasks, setTasks] = useState([]);
    const [, setConflicts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [moveBannerDismissed, setMoveBannerDismissed] = useState(false);
    const [pinSaving, setPinSaving] = useState(false);
    const [pinMessage, setPinMessage] = useState('');
    const [orderSaving, setOrderSaving] = useState(false);
    const [orderMessage, setOrderMessage] = useState('');

    const unseenMoves = useMemo(
        () => tasks.filter((t) => t.schedule_move?.is_unseen),
        [tasks]
    );

    const loadTimeline = useCallback(async ({ silent = false } = {}) => {
        if (!userId || !accessToken) return;
        if (!silent) {
            setLoading(true);
            setError('');
        } else {
            setRefreshing(true);
        }
        try {
            const response = await axios.get(
                `${config.apiBASEURL}/TaskBoardRoutes/gantt/user/${userId}`,
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            setTasks(stampGanttQueueOrder(response.data?.tasks || []));
            setConflicts(response.data?.conflicts || []);
        } catch (err) {
            console.error('Error loading user gantt:', err);
            if (!silent) setError('Could not load your timeline. Please try again.');
        } finally {
            if (!silent) setLoading(false);
            else setRefreshing(false);
        }
    }, [userId, accessToken]);

    const acknowledgeMoves = useCallback(async () => {
        if (!userId || !accessToken) return;
        try {
            await axios.post(
                `${config.apiBASEURL}/TaskBoardRoutes/schedule/acknowledge-moves`,
                { user_id: userId },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            setTasks((prev) =>
                prev.map((t) =>
                    t.schedule_move?.is_unseen
                        ? {
                              ...t,
                              schedule_move: { ...t.schedule_move, is_unseen: false },
                          }
                        : t
                )
            );
            setMoveBannerDismissed(true);
        } catch (err) {
            console.warn('Could not acknowledge schedule moves', err);
        }
    }, [userId, accessToken]);

    useEffect(() => {
        loadTimeline();
    }, [loadTimeline]);

    const { rangeStart, rangeEnd } = useTeamStyleTimeline(tasks, 1);

    const selfUser = useMemo(() => {
        const parts = String(username || 'Me').trim().split(/\s+/);
        return {
            user_id: userId,
            first_name: parts[0] || 'Me',
            last_name: parts.slice(1).join(' ') || '',
        };
    }, [userId, username]);

    const members = useMemo(
        () => [{
            user: selfUser,
            tasks: tasks.map((t) => ({
                ...t,
                gantt_user_id: t.gantt_user_id || userId,
            })),
        }],
        [selfUser, tasks, userId]
    );

    const openCard = (boardId, cardId) => {
        if (!boardId || !cardId) return;
        navigate(`/Graphe/dashboard/kanbanboard/${boardId}?card=${cardId}`);
    };

    const handleDragEnd = async (result) => {
        if (!result.destination || result.source.index === result.destination.index) return;
        const reordered = restampGanttQueueOrder(
            reorderList(tasks, result.source.index, result.destination.index)
        );
        setTasks(reordered);
        if (!userId || !accessToken) return;
        setOrderSaving(true);
        setOrderMessage('');
        setPinMessage('');
        try {
            await axios.post(
                `${config.apiBASEURL}/TaskBoardRoutes/schedule/manual-order`,
                {
                    user_id: Number(userId),
                    ordered_card_ids: reordered.map((t) => t.id),
                },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            setOrderMessage('Task order saved — higher items run first on your timeline.');
            await loadTimeline({ silent: true });
        } catch (err) {
            setOrderMessage(err.response?.data?.message || 'Could not save order.');
            await loadTimeline({ silent: true });
        } finally {
            setOrderSaving(false);
        }
    };

    const savePinStart = async (task, preferredStartAt, allocatedHours = null) => {
        if (!userId || !accessToken || !task?.id) return false;
        if (preferredStartAt) {
            setTasks((prev) =>
                applyOptimisticPinToTasks(prev, task.id, preferredStartAt, userId, allocatedHours, false)
            );
        }
        setPinSaving(true);
        setPinMessage('');
        try {
            const pinRes = await axios.post(
                `${config.apiBASEURL}/TaskBoardRoutes/schedule/pin-start`,
                {
                    user_id: Number(userId),
                    card_id: Number(task.id),
                    preferred_start_at: preferredStartAt,
                    allow_outside_window: true,
                    bump_others: false,
                    ...(allocatedHours != null ? { allocated_hours: allocatedHours } : {}),
                },
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            setPinMessage(
                pinRes.data?.message ||
                    (allocatedHours != null
                        ? `Updated “${task.title}” duration to ${allocatedHours}h.`
                        : `Pinned “${task.title}”.`)
            );
            return true;
        } catch (err) {
            setPinMessage(err.response?.data?.message || 'Could not pin start time.');
            await loadTimeline({ silent: true });
            return false;
        } finally {
            setPinSaving(false);
        }
    };

    const queuePanel = (
        <TimelineRunOrder
            tasks={tasks}
            onDragEnd={handleDragEnd}
            droppableId="user-gantt-queue"
        />
    );

    const subtitle = `${userFullName(selfUser)} · ${formatTimelineScopeRange(
        rangeStart,
        rangeEnd
    )} · all projects`;

    return (
        <Row className="body_content user-gantt-page team-planner-page">
            <Col lg={12} className="mb-2">
                <Breadcrumb className="mb-0">
                    <Breadcrumb.Item onClick={() => navigate(-1)} style={{ cursor: 'pointer' }}>
                        <i className="pi pi-angle-left" /> Back
                    </Breadcrumb.Item>
                    <Breadcrumb.Item active>My Timeline</Breadcrumb.Item>
                </Breadcrumb>
            </Col>

            <Col lg={12}>
                <Card>
                    <Card.Body>
                        {loading && !tasks.length && (
                            <div className="text-center py-5">
                                <Spinner animation="border" size="sm" className="me-2" />
                                Loading timeline…
                            </div>
                        )}

                        {!loading && error && <p className="text-danger">{error}</p>}

                        {orderMessage && (
                            <Alert
                                variant={orderMessage.includes('Could not') ? 'danger' : 'success'}
                                className="py-2 small"
                            >
                                {orderMessage}
                            </Alert>
                        )}

                        {pinMessage && (
                            <Alert
                                variant={
                                    /could not|no available|denied|error/i.test(pinMessage)
                                        ? 'danger'
                                        : /busy|moved to the next/i.test(pinMessage)
                                          ? 'warning'
                                          : 'success'
                                }
                                className="py-2 small"
                            >
                                {pinMessage}
                            </Alert>
                        )}

                        {!loading && unseenMoves.length > 0 && !moveBannerDismissed && (
                            <Alert variant="info" className="py-2 small mb-3 user-gantt-move-banner">
                                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                    <span>
                                        <strong>{unseenMoves.length} task(s) were rescheduled</strong>{' '}
                                        on your timeline since your last visit.
                                    </span>
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={acknowledgeMoves}
                                    >
                                        Got it
                                    </Button>
                                </div>
                            </Alert>
                        )}

                        {!loading && !error && tasks.length === 0 && (
                            <p className="text-muted mb-0">No active assigned cards in this range.</p>
                        )}

                        {!error && tasks.length > 0 && (
                            <TeamStyleGanttChart
                                title="My timeline"
                                subtitle={subtitle}
                                activeCount={tasks.length}
                                members={members}
                                skipRegroup
                                conflictPersonName={userFullName(selfUser)}
                                sidePanel={queuePanel}
                                onDateChange={async (ganttTask) => {
                                    const source = ganttTask?.sourceTask;
                                    if (!source) return false;
                                    const start =
                                        ganttTask.start instanceof Date
                                            ? ganttTask.start.toISOString()
                                            : ganttTask.start;
                                    return savePinStart(
                                        source,
                                        start,
                                        ganttTask.isResize ? ganttTask.hours : null
                                    );
                                }}
                                onBarClick={(t) => {
                                    openCard(t?.board_id, t?.id);
                                }}
                                refreshing={refreshing || pinSaving || orderSaving}
                            />
                        )}
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    );
};

export default UserGantt;
